// Captured at app startup; flushed into each feedback submission's environment payload.

export interface TechnicalContext {
  platform: string;
  browser: string;
  browser_version: string;
  os: string;
  device_type: string;
  resolution: string;
  viewport: string;
  url: string;
  route: string;
  online: boolean;
  network_type: string;
  timezone: string;
  language: string;
  time_format: string;
  time: string;
  build_version: string;
}

export interface ConsoleEntry {
  message: string;
  timestamp: number;
}

export interface FailedRequest {
  url: string;
  method: string;
  status: number;
  timestamp: number;
}

// ── Technical context snapshot ──────────────────────────────────────────────

function parseBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edg")) return "Edge";
  return "Chrome";
}

function parseBrowserVersion(): string {
  const match = navigator.userAgent.match(/(Chrome|Firefox|Safari|Edg)[\/ ](\d+)/);
  return match?.[2] ?? "";
}

export function getTechnicalContext(
  overrides?: Partial<Pick<TechnicalContext, 'url' | 'route' | 'build_version'>>
): TechnicalContext {
  const uaData = (navigator as any).userAgentData;
  const conn   = (navigator as any).connection;

  const brands    = uaData?.brands?.filter((b: any) => !b.brand.includes("Not")) ?? [];
  const mainBrand = brands.at(-1);

  return {
    platform:        "web",
    browser:         mainBrand?.brand   ?? parseBrowserName(),
    browser_version: mainBrand?.version ?? parseBrowserVersion(),
    os:              uaData?.platform  ?? navigator.platform,
    device_type:     uaData?.mobile    ? "Mobile" : "Desktop",
    resolution:      `${window.screen.width}x${window.screen.height}`,
    viewport:        `${window.innerWidth}x${window.innerHeight}`,
    url:             overrides?.url   ?? window.location.href,
    route:           overrides?.route ?? window.location.pathname,
    online:          navigator.onLine,
    network_type:    (() => {
                       const type      = conn?.type;
                       const effective = conn?.effectiveType;
                       const isMobile  = uaData?.mobile ?? false;
                       // conn.type works on Android Chrome; desktop Chrome always returns "unknown"
                       const typeLabels: Record<string, string> = {
                         wifi: "WiFi", cellular: "Cellular", ethernet: "Ethernet",
                         bluetooth: "Bluetooth", wimax: "WiMAX", none: "No network",
                       };
                       if (type && typeLabels[type]) return typeLabels[type];
                       // Desktop browsers can't expose WiFi vs Ethernet via API,
                       // but desktops are never on cellular, so this is safe.
                       if (!isMobile && navigator.onLine) return "WiFi / Ethernet";
                       // Mobile fallback: effectiveType is speed-based, not medium-based
                       const effectiveLabels: Record<string, string> = {
                         "slow-2g": "Slow 2G", "2g": "2G", "3g": "3G", "4g": "4G",
                       };
                       return (effective && effectiveLabels[effective]) ? effectiveLabels[effective] : "Unknown";
                     })(),
    timezone:        Intl.DateTimeFormat().resolvedOptions().timeZone,
    language:        navigator.language,
    time_format:     new Intl.DateTimeFormat([], { hour: "numeric" }).resolvedOptions().hour12 === false
                       ? "24-hour"
                       : "12-hour",
    time:            new Date().toLocaleString(navigator.language, {
                       year: "numeric", day: "numeric", month: "short",
                       hour: "2-digit", minute: "2-digit", second: "2-digit",
                     }),
    build_version:   overrides?.build_version ?? process.env.REACT_APP_VERSION ?? "unknown",
  };
}

// ── Console error collector ──────────────────────────────────────────────────

const _consoleErrors: ConsoleEntry[] = [];

export function initErrorCollector(): void {
  const _origError = console.error.bind(console);
  console.error = (...args: any[]) => {
    _consoleErrors.push({ message: args.map(String).join(" "), timestamp: Date.now() });
    _origError(...args);
  };

  window.onerror = (msg, src, line, col) => {
    _consoleErrors.push({
      message:   `${msg} @ ${src}:${line}:${col}`,
      timestamp: Date.now(),
    });
  };

  window.addEventListener("unhandledrejection", (e) => {
    _consoleErrors.push({
      message:   `Unhandled rejection: ${e.reason}`,
      timestamp: Date.now(),
    });
  });
}

export const getConsoleErrors = (): ConsoleEntry[] => [..._consoleErrors];

// ── Failed request collector ─────────────────────────────────────────────────

const _failedRequests: FailedRequest[] = [];

export function initRequestCollector(): void {
  const _origFetch = window.fetch.bind(window);

  // Feebdack's own API origin — requests to this domain are internal and excluded from tracking
  const ownApiOrigin: string =
    (process.env.REACT_APP_API_URL as string | undefined) ||
    (process.env.NODE_ENV === "production" ? "https://api.cluva.io" : "https://api.cluva.in");

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Fetches marked __feebdack_internal (connectivity checks, version probes, own API)
    // bypass the collector entirely and go straight to the real fetch.
    const isInternal = !!(init as any)?.__feebdack_internal;
    const url    = typeof input === "string" ? input : (input as Request).url;
    const isOwnApi = url.startsWith(ownApiOrigin);
    if (isInternal || isOwnApi) return _origFetch(input, init);

    const method = init?.method ?? "GET";
    try {
      const res = await _origFetch(input, init);
      // Skip opaque no-cors responses — they always have ok: false / status: 0 but are
      // connectivity probes, not genuine failures of the user's app.
      if (!res.ok && res.type !== "opaque") {
        _failedRequests.push({ url, method, status: res.status, timestamp: Date.now() });
      }
      return res;
    } catch (err) {
      _failedRequests.push({ url, method, status: 0, timestamp: Date.now() });
      throw err;
    }
  };
}

export const getFailedRequests = (): FailedRequest[] => [..._failedRequests];
